<script>
import navigation from "../global/navigation.vue";
import MarkdownIt from "markdown-it";
import { getArticleBySlug, getRelatedArticles } from "../../kb";

// html: true — article bodies are our own migrated content (embedded video iframes)
const md = new MarkdownIt({ html: true, linkify: true });

export default {
  name: "kb-article",
  components: {
    navigation,
  },
  data() {
    return {
      article: null,
      related: [],
    };
  },
  computed: {
    renderedBody() {
      if (!this.article) {
        return "";
      }
      // cross-links between migrated articles stay inside the app;
      // everything else external opens in a new tab
      return md.render(this.article.body)
          .replace(
              /href="https?:\/\/kb\.ashoreapp\.com\/([^/"?#]+)\/?[^"]*"/g,
              (match, slug) =>
                  getArticleBySlug(slug)
                      ? 'href="/knowledge-base-article?slug=' + slug + '"'
                      : match
          )
          .replace(/<a href="http/g, '<a target="_blank" rel="noopener" href="http');
    },
  },
  watch: {
    "$route.query.slug": {
      immediate: true,
      handler(slug) {
        this.article = slug ? getArticleBySlug(slug) : null;
        this.related = this.article ? getRelatedArticles(this.article) : [];
        if (this.$refs.articleScroll) {
          this.$refs.articleScroll.scrollTop = 0;
        }
      },
    },
  },
  methods: {
    goBack() {
      this.$router.push("/knowledge-base");
    },
    goToCategory() {
      this.$router.push("/knowledge-base?category=" + encodeURIComponent(this.article.categorySlug));
    },
    openArticle(article) {
      this.$router.push("/knowledge-base-article?slug=" + encodeURIComponent(article.slug));
    },
    onBodyClick(event) {
      const link = event.target.closest ? event.target.closest("a") : null;
      const href = link && link.getAttribute("href");
      if (href && href.startsWith("/knowledge-base-article")) {
        event.preventDefault();
        if (href !== this.$route.fullPath) {
          this.$router.push(href);
        }
      }
    },
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />
    <div class="page-body">
      <div class="proof-shell">
        <!-- Info rail -->
        <aside class="info-rail">
          <div class="rail-scroll">
            <button class="back-link" @click="goBack">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              Knowledge Base
            </button>

            <template v-if="article">
              <div class="rail-hero">
                <h1 class="article-rail-title">{{ article.title }}</h1>
                <button class="status-badge category-badge" @click="goToCategory">
                  <span class="status-dot" style="background: #019256;"></span>
                  {{ article.category }}
                </button>
                <p class="article-meta">Updated {{ article.updatedAt }}</p>
              </div>

              <div v-if="related.length" class="rail-group">
                <p class="rail-label">Related Articles</p>
                <ul class="related-list list-unstyled">
                  <li v-for="rel in related" :key="rel.slug">
                    <a
                        class="related-link"
                        :href="'/knowledge-base-article?slug=' + rel.slug"
                        @click.prevent="openArticle(rel)"
                    >{{ rel.title }}</a>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </aside>

        <!-- Main column -->
        <section class="proof-main-col">
          <div ref="articleScroll" class="content-view">
            <div class="col-inner">
              <article v-if="article" class="detail-card">
                <h1 class="article-title">{{ article.title }}</h1>
                <div class="article-body" v-html="renderedBody" @click="onBodyClick"></div>
                <footer class="article-cta">
                  <div class="article-cta-text">
                    <p class="article-cta-headline">Still have questions?</p>
                    <p class="article-cta-sub">We're happy to help with any questions or concerns.</p>
                  </div>
                  <a
                      href="https://ashoreapp.com/contact/"
                      target="_blank"
                      rel="noopener"
                      class="article-cta-btn"
                  >Contact Us</a>
                </footer>
              </article>
              <div v-else class="noresults-wrap">
                <p class="noresults-headline">Article not found</p>
                <p class="noresults-sub">This article may have been moved or removed.</p>
                <button class="back-link noresults-back" @click="goBack">Back to Knowledge Base</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-body {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
}
@media (max-width: 991px) {
  .page-body {
    height: auto;
    min-height: 100vh;
  }
}

.proof-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  background: #fff;
  position: relative;
}

/* --- Info rail --- */
.info-rail {
  width: 288px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
@media (max-width: 767px) {
  .proof-shell { flex-direction: column; }
  .info-rail {
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }
}

.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: thin;
}
.rail-scroll::-webkit-scrollbar { width: 8px; }
.rail-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  border: 2px solid #fbfbfc;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.back-link:hover { background: rgba(1, 146, 86, 0.08); color: #019256; }

.rail-hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 0 10px;
}
.article-rail-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  margin: 0;
}
.article-meta {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
}
.category-badge {
  border: 0;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.category-badge:hover { background: rgba(1, 146, 86, 0.1); color: #019256; }
.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}

.rail-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 10px;
}
.rail-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}
.related-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
}
.related-link {
  display: block;
  padding: 6px 8px;
  margin: 0 -8px;
  border-radius: 6px;
  color: #1f2937;
  font-size: 13px;
  line-height: 1.4;
  text-decoration: none;
  transition: background-color 120ms ease, color 120ms ease;
}
.related-link:hover {
  background: rgba(1, 146, 86, 0.08);
  color: #019256;
  text-decoration: none;
}

/* --- Main column --- */
.proof-main-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #f4f5f7;
}
.content-view {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: transparent;
}
.col-inner {
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}

.detail-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  padding: 40px 48px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
@media (max-width: 767px) {
  .detail-card { padding: 24px 20px; }
}

.article-title {
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  margin: 0 0 24px;
}

/* Rendered markdown */
.article-body {
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
}
.article-body >>> h1,
.article-body >>> h2 {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 32px 0 12px;
}
.article-body >>> h3,
.article-body >>> h4 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 24px 0 10px;
}
.article-body >>> p { margin: 0 0 16px; }
.article-body >>> a { color: #019256; }
.article-body >>> a:hover { color: #017a48; }
.article-body >>> ul,
.article-body >>> ol {
  margin: 0 0 16px;
  padding-left: 24px;
}
.article-body >>> li { margin-bottom: 6px; }
.article-body >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  margin: 8px 0 16px;
}
.article-body >>> iframe {
  max-width: 100%;
  border-radius: 8px;
  margin: 8px 0 16px;
}
.article-body >>> blockquote {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-left: 3px solid #019256;
  background: rgba(1, 146, 86, 0.05);
  border-radius: 0 8px 8px 0;
  color: #4b5563;
}
.article-body >>> blockquote p:last-child { margin-bottom: 0; }
.article-body >>> code {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}
.article-body >>> pre {
  background: #f3f4f6;
  padding: 14px 16px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 0 0 16px;
}
.article-body >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 16px;
  font-size: 14px;
}
.article-body >>> th,
.article-body >>> td {
  border: 1px solid rgba(15, 23, 42, 0.1);
  padding: 8px 12px;
  text-align: left;
}
.article-body >>> th { background: #fbfbfc; }
.article-body >>> hr {
  border: 0;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  margin: 24px 0;
}

/* Contact CTA footer */
.article-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 40px;
  padding: 20px 24px;
  background: rgba(1, 146, 86, 0.05);
  border: 1px solid rgba(1, 146, 86, 0.15);
  border-radius: 10px;
}
.article-cta-headline {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 2px;
}
.article-cta-sub {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.article-cta-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.article-cta-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}

/* Not-found state */
.noresults-wrap {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  padding: 64px 24px;
  text-align: center;
}
.noresults-headline {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
}
.noresults-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 16px;
}
.noresults-back {
  display: inline-flex;
  border: 1px solid rgba(15, 23, 42, 0.12);
}
</style>
